import { yupResolver } from '@hookform/resolvers/yup';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { validatePassword, validatePhoneNumber } from 'utils';
import { Input, PhoneInput } from 'components';

import styles from './Authentication.module.scss';

export type SignUpProps = {
  onSubmitted?(username: string): void;
  onOpenLogin?(): void;
};

export type FormValues = {
  name: string;
  phoneNumber: string;
  password: string;
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Full name is required').max(50, 'Full name is too long'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .test('isValid', 'Phone number is invalid', value => !!value && validatePhoneNumber(value)),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password cannot be shorter than 8 characters')
    .test(
      'isValidPassword',
      'Password should contain uppercase and lowercase letters',
      value => !!value && validatePassword(value)
    )
});

const SignUp = ({ onSubmitted, onOpenLogin }: SignUpProps) => {
  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      password: ''
    }
  });

  const handleOpenLogin = () => onOpenLogin?.();

  const handleOnSubmit = async (validValues: FormValues) => {
    const { name, phoneNumber, password } = validValues;

    try {
      const username = phoneNumber.slice(1);

      await Auth.signUp({
        username,
        password,
        attributes: { name, phone_number: phoneNumber },
        // @ts-ignore:
        autoSignIn: { enabled: true }
      });

      onSubmitted?.(username);
    } catch (e) {
      if (e instanceof Object && 'message' in e) {
        const parsedError = e as { message };

        form.setError('password', { message: parsedError.message, type: 'server' });
      }
    }
  };

  return (
    <div className={styles.signUp}>
      <form className={styles.signUp} onSubmit={form.handleSubmit(handleOnSubmit)}>
        <div className={styles.fields}>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <Input
                className={styles.name}
                invalid={!!fieldState.error?.message}
                feedback={fieldState.error?.message}
                placeholder="Full name"
                {...field}
              />
            )}
          />

          <Controller
            control={form.control}
            name="phoneNumber"
            render={({ field, fieldState }) => (
              <PhoneInput
                className={styles.phone}
                invalid={!!fieldState?.error?.message}
                feedback={fieldState?.error?.message}
                placeholder="Your phone number"
                value={field.value}
                onChange={value => form.setValue('phoneNumber', value)}
              />
            )}
          />

          <Controller
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <Input
                className={styles.password}
                invalid={!!fieldState.error?.message}
                feedback={fieldState.error?.message}
                type="password"
                placeholder="Password"
                {...field}
              />
            )}
          />
        </div>

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className={cx({ [styles.active]: form.formState.isSubmitted })}
        >
          Next
        </button>

        <div className={styles.footer}>
          <span className={styles.mention}>Already have a Livetalks account?</span>
          <button type="button" className={styles.action} onClick={handleOpenLogin}>
            Log In
          </button>
        </div>
      </form>
    </div>
  );
};

export default SignUp;
